import { useCallback, useContext, useEffect, useRef } from "react";
import EnumsContext from "../context/enums-context";
import useAuthenticatedRequest from "./TokenrRefreshUtils";

const useGetEnums = (types = []) => {
    const { enums, setEnum } = useContext(EnumsContext);
    const authenticatedRequest = useAuthenticatedRequest();
    const loadingRef = useRef({});

    const fetchEnum = useCallback(async (type) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}enums/${type}/`;
        return authenticatedRequest(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        });
    }, [authenticatedRequest]);

    const fetchAndSetEnums = useCallback(async (targetTypes = types) => {

        const converters = {
            product_groups: data => data.map(i => ({ value: i.id, label: i.group_name })),
            divisions: data => data.map(i => ({ value: i.id, label: i.name })),
            financial_data_types: data => data.map(i => ({ value: i.name, label: i.value })),
            cagent_types: data => data.map(i => ({ value: i.name, label: i.value })),
            partners: data => data.map(i => ({ value: i.id, label: i.name })),
            pay_statuses: data => data.map(i => ({ value: i.name, label: i.value })),
            service_groups: data => data.map(i => ({ value: i.id, label: i.group_name })),
            clients: data => data.map(i => ({ value: i.id, label: i.name })),
            users: data => data.map(i => ({ value: i.id, label: i.full_name })),
            brands_dictionary: data => data.map(i => ({ value: i.id, label: i.name }))
        };

        for (const type of targetTypes) {
            if (!type) continue;

            const enumState = enums[type];

            // already loaded OR currently loading → skip
            if (enumState?.loaded || loadingRef.current[type]) {
                continue;
            }

            loadingRef.current[type] = true;

            try {
                const response = await fetchEnum(type);

                console.log(type + " fetched");

                if (response.status !== 200) {
                    setEnum(type, []); // loaded but empty
                    continue;
                }

                const data = await response.json();
                const converter = converters[type];

                setEnum(type, converter ? converter(data) : data);
            } catch (err) {
                console.error(`Failed to load enum: ${type}`, err);
                setEnum(type, []);
            } finally {
                loadingRef.current[type] = false;
            }
        }
    }, [types, enums, fetchEnum, setEnum]);

    useEffect(() => {
        if (types.length > 0) {
            fetchAndSetEnums(types);
        }
    }, [types, fetchAndSetEnums]);

    return {
        reloadEnums: fetchAndSetEnums
    };
};

export default useGetEnums;